import { create } from 'zustand';
import api from '../api/client';
import { getEcho } from '../lib/echo';

/**
 * Menü bildirim rozetleri için tek kaynak (singleton) store.
 *
 * Önceki `useMenuNotifications` hook'u her tüketicide (Sidebar, MobileBottomNav,
 * SiteTimelinePage) ayrı ayrı 60 sn polling + aynı private kanala ayrı abonelik
 * açıyordu; ayrıca bir tüketici unmount olunca `echo.leave` ortak kanalı herkes
 * için kapatıyordu. Store yaşam döngüsü tüketici sayısına (consumerCount) bağlıdır:
 * ilk tüketici init eder, son tüketici ayrılınca kaynaklar serbest bırakılır.
 */

interface NotificationReceivedPayload {
  notification: unknown;
  unread_count: number;
  menu_key?: string | null;
}

interface MenuNotificationState {
  counts: Record<string, number>;
  activeUserId: number | null;
  initialized: boolean;
  consumerCount: number;
  init: (userId: number) => void;
  release: () => void;
  markRead: (key?: string) => Promise<void>;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let subscribedChannel: string | null = null;

const fetchCounts = async () => {
  try {
    const res = await api.get('/api/v1/menu-notifications');
    useMenuNotificationStore.setState({ counts: res.data?.data || {} });
  } catch {
    // ignore
  }
};

const subscribeRealtime = (userId: number) => {
  const echo = getEcho();
  if (!echo) return;
  const channelName = `App.Models.User.${userId}`;
  echo.private(channelName).listen('.notification.received', (e: NotificationReceivedPayload) => {
    if (e?.menu_key) {
      const key = e.menu_key;
      useMenuNotificationStore.setState((s) => ({
        counts: { ...s.counts, [key]: (s.counts[key] || 0) + 1 },
      }));
    }
  });
  subscribedChannel = channelName;
};

const teardownInternal = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (subscribedChannel) {
    const echo = getEcho();
    if (echo) {
      echo.leave(subscribedChannel);
    }
    subscribedChannel = null;
  }
};

export const useMenuNotificationStore = create<MenuNotificationState>((set, get) => ({
  counts: {},
  activeUserId: null,
  initialized: false,
  consumerCount: 0,

  init: (userId: number) => {
    const state = get();
    // Aynı kullanıcı için zaten çalışıyorsa sadece tüketici sayısını artır.
    if (state.initialized && state.activeUserId === userId) {
      set({ consumerCount: state.consumerCount + 1 });
      return;
    }
    // Kullanıcı değişimi veya ilk kurulum: eski kaynakları temizle.
    teardownInternal();
    set({
      counts: {},
      activeUserId: userId,
      initialized: true,
      consumerCount: state.consumerCount + 1,
    });
    fetchCounts();
    pollTimer = setInterval(fetchCounts, 60000);
    subscribeRealtime(userId);
  },

  release: () => {
    const state = get();
    const consumerCount = Math.max(0, state.consumerCount - 1);
    if (consumerCount > 0) {
      set({ consumerCount });
      return;
    }
    teardownInternal();
    set({ counts: {}, activeUserId: null, initialized: false, consumerCount: 0 });
  },

  markRead: async (key?: string) => {
    if (!key) return;
    const { counts } = get();
    if (!counts[key]) return;
    try {
      await api.post(`/api/v1/menu-notifications/${key}/read`);
      set((s) => ({ counts: { ...s.counts, [key]: 0 } }));
    } catch {
      // ignore
    }
  },
}));
